import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Arrowupward } from '@dsny/dsny-component-library/icons'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  BackToTopButtonRoot,
  BackToTopLabel
} from './BackToTopButton.styles'

const DEFAULT_PLAYBAR_HEIGHT = {
  mobile: 72,
  desktop: 96
}

const SCROLL_CONTAINER_SELECTOR = '.content-scrollable-area'
const PLAYBAR_SELECTOR = '[data-playbar-root="true"]'

export default function BackToTopButton({ promoTitleRef }) {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const [isVisible, setIsVisible] = useState(false)
  const [bottomOffset, setBottomOffset] = useState(
    DEFAULT_PLAYBAR_HEIGHT.desktop + 24
  )

  useEffect(() => {
    const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR)

    if (!scrollContainer) {
      return undefined
    }

    const getPromoTitle = () => promoTitleRef?.current
    const getPlaybar = () => document.querySelector(PLAYBAR_SELECTOR)

    const evaluateVisibility = () => {
      const promoTitle = getPromoTitle()
      const playbar = getPlaybar()
      const spacing = isMobile ? 16 : 24
      const playbarHeight =
        playbar?.getBoundingClientRect().height ||
        (isMobile
          ? DEFAULT_PLAYBAR_HEIGHT.mobile
          : DEFAULT_PLAYBAR_HEIGHT.desktop)

      setBottomOffset(playbarHeight + spacing)

      const scrolledPastFourScreens =
        scrollContainer.scrollTop >= scrollContainer.clientHeight * 4

      const promoReachedTop = promoTitle
        ? promoTitle.getBoundingClientRect().top <=
          scrollContainer.getBoundingClientRect().top + 1
        : false

      setIsVisible(scrolledPastFourScreens || promoReachedTop)
    }

    const rafId = window.requestAnimationFrame(evaluateVisibility)
    scrollContainer.addEventListener('scroll', evaluateVisibility, {
      passive: true
    })
    window.addEventListener('resize', evaluateVisibility)

    let playbarResizeObserver
    const playbar = getPlaybar()
    if (playbar && typeof ResizeObserver !== 'undefined') {
      playbarResizeObserver = new ResizeObserver(evaluateVisibility)
      playbarResizeObserver.observe(playbar)
    }

    return () => {
      window.cancelAnimationFrame(rafId)
      scrollContainer.removeEventListener('scroll', evaluateVisibility)
      window.removeEventListener('resize', evaluateVisibility)
      playbarResizeObserver?.disconnect()
    }
  }, [isMobile, promoTitleRef])

  const handleScrollToTop = () => {
    const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR)
    scrollContainer?.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  return (
    <BackToTopButtonRoot
      type="button"
      aria-label={t('general.back-to-top')}
      data-visible={isVisible}
      $bottomOffset={bottomOffset}
      $isMobile={isMobile}
      $isVisible={isVisible}
      onClick={handleScrollToTop}
    >
      <Arrowupward width={16} height={16} />
      {!isMobile && <BackToTopLabel>{t('general.back-to-top')}</BackToTopLabel>}
    </BackToTopButtonRoot>
  )
}
